import { isFullvisioSlotType } from '@omecare/utils';
import { VideoIcon } from 'lucide-react';
import { useTranslation } from 'react-i18next';
import { Sheet } from '@/components/ui/sheet';
import { GetWorkScheduleSlotItemResponse } from '@/hooks/queries/use-get-office-work-schedules-slots';
import { useDateFnsLocale } from '@/hooks/use-date-fns-locale';
import { capitalizeFirst } from '@/lib/capitalize-first';
import { selectSheetViewModel } from '../sheet.viewmodel';
import { formatSlotSchedule } from '../slot-sheet/format-slot-schedule';
import { SlotSheetHeader } from '../slot-sheet/slot-sheet-header';
import { SlotSheetMetaRow } from '../slot-sheet/slot-sheet-meta-row';
import {
  SlotSheetDefinitionList,
  SlotSheetRow,
  SlotSheetSection,
} from '../slot-sheet/slot-sheet-section';
import {
  SlotSheetBody,
  SlotSheetContent,
} from '../slot-sheet/slot-sheet-shell';
import { ViewWorkScheduleSlotEventOccurrence } from '../view-admin-work-schedule-slot-sheet/view-admin-work-schedule-slot-sheet.viewmodel';
import { selectViewUserWorkScheduleSlotSheetViewModel } from './view-user-work-schedule-slot-sheet.viewmodel';

/**
 * Read-only view of an attending doctor's planned presence ("créneau
 * titulaire"), shown to substitutes. It carries no edit action and no contact
 * details — only when the doctor works.
 */
export const ViewUserWorkScheduleSlotSheet = ({
  slot,
  eventOccurrence,
}: {
  slot: GetWorkScheduleSlotItemResponse;
  eventOccurrence: ViewWorkScheduleSlotEventOccurrence;
}) => {
  const { t } = useTranslation();
  const locale = useDateFnsLocale();
  const sheetViewModel = selectSheetViewModel({
    sheetName: 'ViewUserWorkScheduleSlotSheet',
  });

  const viewModel = selectViewUserWorkScheduleSlotSheetViewModel({ slot });

  const schedule = formatSlotSchedule({
    start: eventOccurrence.start,
    end: eventOccurrence.end,
    allDay: eventOccurrence.allday,
    locale,
  });

  return (
    <Sheet
      open={sheetViewModel.data.show}
      onOpenChange={sheetViewModel.api.onOpenChange}
    >
      <SlotSheetContent size="detail">
        <SlotSheetHeader
          status="availability"
          title={t(viewModel.title.key, viewModel.title.params)}
          dateLabel={schedule.dateLabel}
          timeLabel={schedule.timeLabel}
        >
          <SlotSheetMetaRow
            id={slot.id}
            updatedAt={slot.updatedAt}
          />

          <p className="text-muted-foreground text-xs">
            {t('view-user-work-schedule-slot-sheet.description')}
          </p>
        </SlotSheetHeader>

        <SlotSheetBody>
          <SlotSheetSection title={t('slot-sheet.section.slot')}>
            <SlotSheetDefinitionList>
              <SlotSheetRow label={t('slot-sheet.field.period')}>
                {capitalizeFirst(t(`period-of-day.${slot.periodOfTheDay}`))}
                {schedule.timeLabel && (
                  <span className="text-muted-foreground">
                    {' '}
                    · {schedule.timeLabel}
                  </span>
                )}
              </SlotSheetRow>

              <SlotSheetRow
                label={t('view-user-agenda-slot-sheet.information.slot-type')}
              >
                <span className="flex items-center gap-1.5">
                  {isFullvisioSlotType(viewModel.information.slotType) && (
                    <VideoIcon
                      className="size-3.5 text-muted-foreground"
                      aria-hidden="true"
                    />
                  )}
                  {t(`agenda-slot.types.${viewModel.information.slotType}`)}
                </span>
              </SlotSheetRow>
            </SlotSheetDefinitionList>
          </SlotSheetSection>
        </SlotSheetBody>
      </SlotSheetContent>
    </Sheet>
  );
};
